import React, { createContext, useContext, useRef, useState } from 'react';

const SendJobsContext = createContext(null);

export function SendJobsProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const seenIdxRef = useRef({});
  const failedCountRef = useRef({});

  // Lead outreach jobs live in a different job store (server/services/jobStore.js)
  // that returns the full log + status each poll instead of incremental events,
  // so they get their own poll loop but share the same floating card.
  const startLeadPolling = (jobId) => {
    failedCountRef.current[jobId] = 0;

    const poll = async () => {
      try {
        const res = await fetch(`/api/leads/send/status/${jobId}`);
        // 404 = server restarted and the in-memory job is gone — stop polling.
        const lost = res.status === 404;
        if (!res.ok && !lost) throw new Error('status error');
        const data = lost ? {} : await res.json();
        const done = lost || data.status === 'done' || data.status === 'error';
        const rawLogs = data.logs || [];

        setJobs(prev => prev.map(j => {
          if (j.jobId !== jobId) return j;
          const sent = data.sent ?? j.sent;
          const failed = data.failed ?? j.failed;
          const log = lost ? [...j.log, { ok: false, msg: '✕ Lost track of this send (server restarted)' }]
            : rawLogs.map(msg => ({ ok: msg.startsWith('✓') ? true : (msg.startsWith('✕') || msg.startsWith('Error')) ? false : null, msg }));
          failedCountRef.current[jobId] = lost ? 1 : failed;
          return { ...j, sent, failed, total: data.total ?? j.total, log: log.slice(-20), rawLogs: lost ? j.rawLogs : rawLogs, done };
        }));

        if (!done) {
          setTimeout(poll, 2000);
        } else if (failedCountRef.current[jobId] === 0) {
          setTimeout(() => {
            setJobs(prev => prev.filter(j => j.jobId !== jobId));
            delete failedCountRef.current[jobId];
          }, 8000);
        }
      } catch {
        setTimeout(poll, 3000);
      }
    };

    setTimeout(poll, 1000);
  };

  const startPolling = (jobId) => {
    seenIdxRef.current[jobId] = 0;
    failedCountRef.current[jobId] = 0;

    const poll = async () => {
      const since = seenIdxRef.current[jobId] ?? 0;
      try {
        const res = await fetch(`/api/pitching/status/${jobId}?since=${since}`);
        if (!res.ok) throw new Error('status error');
        const data = await res.json();

        seenIdxRef.current[jobId] = data.total ?? since;

        setJobs(prev => {
          const idx = prev.findIndex(j => j.jobId === jobId);
          if (idx === -1) return prev;
          const j = prev[idx];
          const newLog = [...j.log];
          let sent = j.sent;
          let failed = j.failed;
          const warnings = [...(j.warnings || [])];
          (data.events || []).forEach(ev => {
            if (ev.type === 'sent') {
              newLog.push({ ok: true, msg: `✓ ${ev.podcastName}` });
              if (ev.sheetWarning) warnings.push(`⚠ ${ev.podcastName}: ${ev.sheetWarning}`);
              if (ev.sent != null) sent = ev.sent;
            } else if (ev.type === 'error') {
              newLog.push({ ok: false, msg: `✕ ${ev.podcastName}: ${ev.message}` });
              if (ev.failed != null) failed = ev.failed;
            } else if (ev.type === 'waiting') {
              newLog.push({ ok: null, msg: `⏱ Waiting ${ev.delaySecs}s before next...` });
            }
          });
          const done = data.done || data.jobLost || false;
          const updated = [...prev];
          updated[idx] = { ...j, sent, failed, warnings, log: newLog.slice(-20), done };
          // Sheet-write warnings keep the card up too, same as failures
          failedCountRef.current[jobId] = failed + warnings.length;
          return updated;
        });

        if (!data.done && !data.jobLost) {
          setTimeout(poll, 2000);
        } else if (failedCountRef.current[jobId] === 0) {
          setTimeout(() => {
            setJobs(prev => prev.filter(j => j.jobId !== jobId));
            delete seenIdxRef.current[jobId];
            delete failedCountRef.current[jobId];
          }, 8000);
        }
        // If anything failed, leave the job card up until the user dismisses it —
        // don't let a send failure disappear unnoticed 8 seconds later.
      } catch {
        setTimeout(poll, 3000);
      }
    };

    setTimeout(poll, 1000);
  };

  // kind: 'pitch' (podcast outreach, default) or 'lead' (lead outreach)
  const addJob = (jobId, clientName, total, kind = 'pitch') => {
    setJobs(prev => [...prev, { jobId, kind, clientName, sent: 0, failed: 0, total, done: false, log: [], rawLogs: [] }]);
    if (kind === 'lead') startLeadPolling(jobId);
    else startPolling(jobId);
  };

  const cancelJob = async (jobId) => {
    const job = jobs.find(j => j.jobId === jobId);
    const url = job?.kind === 'lead' ? `/api/leads/send/cancel/${jobId}` : `/api/pitching/cancel/${jobId}`;
    try {
      await fetch(url, { method: 'POST' });
    } catch {}
    // Lead jobs keep polling until the server confirms the stop (it finishes
    // the current wait/send first), so only pitch jobs are marked done here.
    if (job?.kind !== 'lead') {
      setJobs(prev => prev.map(j => j.jobId === jobId ? { ...j, done: true } : j));
    }
  };

  const dismissJob = (jobId) => {
    setJobs(prev => prev.filter(j => j.jobId !== jobId));
    delete seenIdxRef.current[jobId];
    delete failedCountRef.current[jobId];
  };

  return (
    <SendJobsContext.Provider value={{ jobs, addJob, cancelJob, dismissJob }}>
      {children}
    </SendJobsContext.Provider>
  );
}

export function useSendJobs() {
  return useContext(SendJobsContext);
}
